/**
 * contentApi: client service for per-ecosystem authored page content.
 *
 * Wraps the tenant-scoped EcosystemContent endpoint. The public GET returns
 * the PUBLISHED content for the current ecosystem and a given page key:
 *
 *   GET /api/ecosystem-content/:pageKey
 *     -> { pageKey, blocks, publishedAt, published }
 *
 * The endpoint is unauthenticated, but the request still goes through the
 * tenant-aware axios instance so that, in path mode, the interceptor prepends
 * the active `basePath` (`/<slug>`): the server's tenant-resolution
 * middleware needs that prefix to scope the lookup to the right ecosystem.
 *
 * P5 wave 3 (admin content editor) adds the admin-only draft / publish calls:
 *
 *   GET  /api/ecosystem-content/:pageKey/draft
 *     -> { pageKey, draftBlocks, blocks, publishedAt }
 *   PUT  /api/ecosystem-content/:pageKey/draft   body: { blocks }
 *     -> { pageKey, draftBlocks }
 *   POST /api/ecosystem-content/:pageKey/publish
 *     -> { pageKey, blocks, publishedAt, published }
 *
 * These three require an `admin` EcosystemMembership server-side
 * (isAdminOfCurrentEcosystem); the same authenticated, tenant-aware axios
 * instance carries the Firebase token and the tenant basePath.
 */
import { useMemo } from 'react';

import { useAuthenticatedAxios } from './axiosInstance';

export const useContentApi = () => {
    const api = useAuthenticatedAxios();

    return useMemo(
        () => ({
            /**
             * Fetch the published content for a page in the current
             * ecosystem.
             *
             * @param {string} pageKey - One of 'landing' | 'about' | 'mission'.
             * @returns {Promise<{pageKey: string, blocks: Array,
             *   publishedAt?: string, published: boolean}>} The published
             *   payload. `published` is false (and `blocks` empty) for an
             *   unauthored or never-published page.
             */
            fetchPublishedContent: async (pageKey) => {
                const response = await api.get(
                    `/api/ecosystem-content/${encodeURIComponent(pageKey)}`
                );
                return response.data;
            },

            /**
             * Fetch the working draft (plus published state) for a page in
             * the current ecosystem. Admin-only on the server.
             *
             * @param {string} pageKey - One of 'landing' | 'about' | 'mission'.
             * @returns {Promise<{pageKey: string, draftBlocks: Array,
             *   blocks: Array, publishedAt: ?string}>} The draft payload.
             */
            fetchDraftContent: async (pageKey) => {
                const response = await api.get(
                    `/api/ecosystem-content/${encodeURIComponent(
                        pageKey
                    )}/draft`
                );
                return response.data;
            },

            /**
             * Replace the working draft for a page in the current ecosystem.
             * Admin-only on the server. Does not affect published content.
             *
             * @param {string} pageKey - One of 'landing' | 'about' | 'mission'.
             * @param {Array} blocks - The full ordered block list to save.
             * @returns {Promise<{pageKey: string, draftBlocks: Array}>} The
             *   saved draft.
             */
            saveDraftContent: async (pageKey, blocks) => {
                const response = await api.put(
                    `/api/ecosystem-content/${encodeURIComponent(
                        pageKey
                    )}/draft`,
                    { blocks }
                );
                return response.data;
            },

            /**
             * Publish the current draft for a page in the current ecosystem,
             * promoting the draft into the live published content. Admin-only
             * on the server.
             *
             * @param {string} pageKey - One of 'landing' | 'about' | 'mission'.
             * @returns {Promise<{pageKey: string, blocks: Array,
             *   publishedAt: string, published: boolean}>} The published
             *   payload.
             */
            publishContent: async (pageKey) => {
                const response = await api.post(
                    `/api/ecosystem-content/${encodeURIComponent(
                        pageKey
                    )}/publish`
                );
                return response.data;
            },
        }),
        [api]
    );
};

export default useContentApi;
